import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWeightRecords } from '@/hooks/useWeightRecords';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { type WeightUnit, toKg, suggestUnit } from '@/utils/weightFormatting';

interface WeightTrackerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  petId: string;
  petName: string;
}

export function WeightTrackerModal({ open, onOpenChange, petId, petName }: WeightTrackerModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { addRecord, latestWeight } = useWeightRecords(petId);
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState<WeightUnit>('kg');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [saving, setSaving] = useState(false);

  // Set default unit based on latest weight
  useEffect(() => {
    if (latestWeight) {
      setUnit(suggestUnit(Number(latestWeight.weight_kg)));
    }
  }, [latestWeight]);

  const isGrams = unit === 'g';
  const minVal = isGrams ? 1 : 0.01;
  const maxVal = isGrams ? 999999 : 1500;
  const stepVal = isGrams ? 1 : 0.01;

  const handleSave = async () => {
    const inputNum = parseFloat(weight);
    if (isNaN(inputNum) || inputNum < minVal || inputNum > maxVal) {
      toast({ title: t('weightTracker.invalidWeight'), variant: 'destructive' });
      return;
    }
    if (!date) {
      toast({ title: t('weightTracker.invalidDate'), variant: 'destructive' });
      return;
    }

    const weightKg = toKg(inputNum, unit);

    setSaving(true);
    try {
      await addRecord.mutateAsync({ weight_kg: weightKg, measurement_date: date });
      toast({ title: t('weightTracker.saved') });
      setWeight('');
      setDate(format(new Date(), 'yyyy-MM-dd'));
      onOpenChange(false);
    } catch (err) {
      toast({ title: t('weightTracker.saveError'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('weightTracker.title')} - {petName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="weight">{t('weightTracker.weightLabel')}</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="weight"
                type="number"
                step={stepVal}
                min={minVal}
                max={maxVal}
                placeholder={isGrams ? t('weightTracker.weightPlaceholderG') : t('weightTracker.weightPlaceholder')}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="flex-1"
              />
              <div className="flex rounded-md border border-input overflow-hidden">
                <button
                  type="button"
                  onClick={() => setUnit('kg')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    unit === 'kg'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  kg
                </button>
                <button
                  type="button"
                  onClick={() => setUnit('g')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    unit === 'g'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  g
                </button>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="date">{t('weightTracker.dateLabel')}</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? t('common.saving') : t('weightTracker.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
